/**
 * MagicBI Design System - Card Component
 * 
 * Versatile card component with multiple variants,
 * interactive states, and flexible content areas.
 */

import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';

// Card variant types
type CardVariant = 'default' | 'elevated' | 'outlined' | 'filled';
type CardSize = 'sm' | 'md' | 'lg';

// Card props interface
export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Card variant */
  variant?: CardVariant;
  /** Card size */
  size?: CardSize;
  /** Whether card is interactive (hover effects) */
  interactive?: boolean;
  /** Whether card is selected */
  selected?: boolean;
  /** Whether card is disabled */
  disabled?: boolean;
  /** Custom padding override */
  padding?: string;
  /** Custom border radius override */
  borderRadius?: string;
  /** Custom background color */
  backgroundColor?: string;
  /** Custom border color */
  borderColor?: string;
  /** Custom shadow */
  shadow?: string;
  /** Full width card */
  fullWidth?: boolean;
  /** Custom as prop for polymorphic behavior */
  as?: keyof JSX.IntrinsicElements | React.ComponentType<any>;
  /** Children elements */
  children: React.ReactNode;
}

// Card header props
export interface CardHeaderProps {
  children: React.ReactNode;
  /** Header actions (buttons, icons, etc.) */
  actions?: React.ReactNode;
  /** Custom class name */
  className?: string;
}

// Card body props
export interface CardBodyProps {
  children: React.ReactNode;
  /** Custom padding */
  padding?: string;
  /** Custom class name */
  className?: string;
}

// Card footer props
export interface CardFooterProps {
  children: React.ReactNode;
  /** Footer actions (buttons, etc.) */
  actions?: React.ReactNode;
  /** Custom class name */
  className?: string;
}

// Get variant styles
const getVariantStyles = (variant: CardVariant, interactive: boolean, selected: boolean, disabled: boolean) => {
  const baseStyles = css`
    border-radius: ${({ theme }) => theme.borders.semantic.card.default};
    transition: ${({ theme }) => theme.transitions.semantic.interactive.button.all};
  `;

  switch (variant) {
    case 'default':
      return css`
        ${baseStyles}
        background-color: ${({ theme }) => theme.colors.surface.primary};
        border: 1px solid ${({ theme }) => theme.colors.border.subtle};
        box-shadow: ${({ theme }) => theme.shadows.component.card.default};
        
        ${interactive && !disabled && css`
          cursor: pointer;
          
          &:hover {
            box-shadow: ${({ theme }) => theme.shadows.component.card.hover};
            transform: translateY(-2px);
            border-color: ${({ theme }) => theme.colors.border.default};
          }
          
          &:active {
            transform: translateY(-1px);
          }
        `}
        
        ${selected && css`
          border-color: ${({ theme }) => theme.colors.interactive.primary};
          box-shadow: ${({ theme }) => theme.shadows.interactive.focus}, ${({ theme }) => theme.shadows.component.card.hover};
        `}
        
        ${disabled && css`
          opacity: 0.6;
          cursor: not-allowed;
          pointer-events: none;
        `}
      `;

    case 'elevated':
      return css`
        ${baseStyles}
        background-color: ${({ theme }) => theme.colors.surface.primary};
        border: none;
        box-shadow: ${({ theme }) => theme.shadows.elevation[3]};
        
        ${interactive && !disabled && css`
          cursor: pointer;
          
          &:hover {
            box-shadow: ${({ theme }) => theme.shadows.elevation[4]};
            transform: translateY(-4px);
          }
          
          &:active {
            transform: translateY(-2px);
          }
        `}
        
        ${selected && css`
          box-shadow: ${({ theme }) => theme.shadows.interactive.focus}, ${({ theme }) => theme.shadows.elevation[4]};
        `}
        
        ${disabled && css`
          opacity: 0.6;
          cursor: not-allowed;
          pointer-events: none;
        `}
      `;

    case 'outlined':
      return css`
        ${baseStyles}
        background-color: transparent;
        border: 2px solid ${({ theme }) => theme.colors.border.default};
        box-shadow: none;
        
        ${interactive && !disabled && css`
          cursor: pointer;
          
          &:hover {
            border-color: ${({ theme }) => theme.colors.border.strong};
            background-color: ${({ theme }) => theme.colors.surface.secondary};
          }
          
          &:active {
            background-color: ${({ theme }) => theme.colors.surface.tertiary};
          }
        `}
        
        ${selected && css`
          border-color: ${({ theme }) => theme.colors.interactive.primary};
          background-color: ${({ theme }) => theme.colors.primary[50]};
        `}
        
        ${disabled && css`
          opacity: 0.6;
          cursor: not-allowed;
          pointer-events: none;
        `}
      `;

    case 'filled':
      return css`
        ${baseStyles}
        background-color: ${({ theme }) => theme.colors.surface.secondary};
        border: 1px solid transparent;
        box-shadow: none;
        
        ${interactive && !disabled && css`
          cursor: pointer;
          
          &:hover {
            background-color: ${({ theme }) => theme.colors.surface.tertiary};
            box-shadow: ${({ theme }) => theme.shadows.component.card.default};
          }
          
          &:active {
            background-color: ${({ theme }) => theme.colors.surface.primary};
          }
        `}
        
        ${selected && css`
          background-color: ${({ theme }) => theme.colors.primary[50]};
          border-color: ${({ theme }) => theme.colors.interactive.primary};
        `}
        
        ${disabled && css`
          opacity: 0.6;
          cursor: not-allowed;
          pointer-events: none;
        `}
      `;

    default:
      return css``;
  }
};

// Get size styles
const getSizeStyles = (size: CardSize) => {
  switch (size) {
    case 'sm':
      return css`
        padding: ${({ theme }) => theme.spacing.component.card.padding.sm};
      `;

    case 'md':
      return css`
        padding: ${({ theme }) => theme.spacing.component.card.padding.md};
      `;

    case 'lg':
      return css`
        padding: ${({ theme }) => theme.spacing.component.card.padding.lg};
      `;

    default:
      return css``;
  }
};

// Styled card component
const StyledCard = styled.div<CardProps>`
  /* Base styles */
  position: relative;
  display: flex;
  flex-direction: column;
  
  /* Full width */
  ${({ fullWidth }) => fullWidth && css`
    width: 100%;
  `}
  
  /* Apply variant styles */
  ${({ variant = 'default', interactive = false, selected = false, disabled = false }) => 
    getVariantStyles(variant, interactive, selected, disabled)
  }
  
  /* Apply size styles */
  ${({ size = 'md' }) => getSizeStyles(size)}
  
  /* Custom overrides */
  ${({ padding }) => padding && css`
    padding: ${padding};
  `}
  
  ${({ borderRadius }) => borderRadius && css`
    border-radius: ${borderRadius};
  `}
  
  ${({ backgroundColor, theme }) => backgroundColor && css`
    background-color: ${backgroundColor.startsWith('#') ? backgroundColor : (theme.colors as any)[backgroundColor] || backgroundColor};
  `}
  
  ${({ borderColor, theme }) => borderColor && css`
    border-color: ${borderColor.startsWith('#') ? borderColor : (theme.colors as any)[borderColor] || borderColor};
  `}
  
  ${({ shadow }) => shadow && css`
    box-shadow: ${shadow};
  `}
`;

// Card header component
const StyledCardHeader = styled.div<CardHeaderProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.component.card.gap};
  
  /* If no actions, don't use space-between */
  ${({ actions }) => !actions && css`
    justify-content: flex-start;
  `}
`;

// Card body component
const StyledCardBody = styled.div<CardBodyProps>`
  flex: 1;
  
  ${({ padding }) => padding && css`
    padding: ${padding};
  `}
`;

// Card footer component
const StyledCardFooter = styled.div<CardFooterProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: ${({ theme }) => theme.spacing.component.card.gap};
  
  /* If no actions, don't use space-between */
  ${({ actions }) => !actions && css`
    justify-content: flex-start;
  `}
`;

// Card image component
export interface CardImageProps {
  src: string;
  alt: string;
  /** Image position */
  position?: 'top' | 'bottom' | 'left' | 'right';
  /** Image height */
  height?: string;
  /** Image width */
  width?: string;
  /** Object fit */
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
  /** Custom class name */
  className?: string;
}

const StyledCardImage = styled.img<CardImageProps>`
  ${({ position = 'top' }) => {
    switch (position) {
      case 'top':
        return css`
          width: 100%;
          border-top-left-radius: inherit;
          border-top-right-radius: inherit;
          margin-bottom: ${({ theme }) => theme.spacing.component.card.gap};
        `;
      case 'bottom':
        return css`
          width: 100%;
          border-bottom-left-radius: inherit;
          border-bottom-right-radius: inherit;
          margin-top: ${({ theme }) => theme.spacing.component.card.gap};
        `;
      case 'left':
        return css`
          height: 100%;
          border-top-left-radius: inherit;
          border-bottom-left-radius: inherit;
          margin-right: ${({ theme }) => theme.spacing.component.card.gap};
        `;
      case 'right':
        return css`
          height: 100%;
          border-top-right-radius: inherit;
          border-bottom-right-radius: inherit;
          margin-left: ${({ theme }) => theme.spacing.component.card.gap};
        `;
      default:
        return css``;
    }
  }}
  
  ${({ height }) => height && css`
    height: ${height};
  `}
  
  ${({ width }) => width && css`
    width: ${width};
  `}
  
  ${({ objectFit = 'cover' }) => css`
    object-fit: ${objectFit};
  `}
`;

// Main Card component
export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ children, ...props }, ref) => {
    return (
      <StyledCard ref={ref} {...props}>
        {children}
      </StyledCard>
    );
  }
);

Card.displayName = 'Card';

// Card subcomponents
export const CardHeader: React.FC<CardHeaderProps> = ({ 
  children, 
  actions, 
  ...props 
}) => (
  <StyledCardHeader actions={actions} {...props}>
    <div>{children}</div>
    {actions && <div>{actions}</div>}
  </StyledCardHeader>
);

export const CardBody: React.FC<CardBodyProps> = ({ children, ...props }) => (
  <StyledCardBody {...props}>
    {children}
  </StyledCardBody>
);

export const CardFooter: React.FC<CardFooterProps> = ({ 
  children, 
  actions, 
  ...props 
}) => (
  <StyledCardFooter actions={actions} {...props}>
    <div>{children}</div>
    {actions && <div>{actions}</div>}
  </StyledCardFooter>
);

export const CardImage: React.FC<CardImageProps> = (props) => (
  <StyledCardImage {...props} />
);

export default Card;
